'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { trackEvent } from '@/lib/analytics/googleAnalytics';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send reset email');
      setSent(true);
      trackEvent('forgot_password_sent', { page_path: '/forgot-password' });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden px-4"
      style={{ background: 'linear-gradient(135deg, #020817 0%, #0F172A 40%, #0a1628 100%)' }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(201,169,97,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      />
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <Link href="/login" className="flex items-center gap-3">
            <div className="relative h-10 w-10 overflow-hidden rounded-lg border border-[#C9A961]/30 bg-[#1E3A5F]/60 p-1">
              <Image src="/assets/images/Untitled_design-27-1772027045051.png" alt="ChronoMind logo" width={32} height={32} className="h-full w-full object-contain" />
            </div>
            <span className="font-heading text-lg font-bold text-white">Chrono<span className="text-[#C9A961]">Mind™</span></span>
          </Link>
        </div>

        <div className="rounded-2xl border border-[#C9A961]/20 p-8 shadow-2xl backdrop-blur-md" style={{ background: 'rgba(30, 58, 95, 0.4)' }}>
          {sent ? (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20 border border-green-500/30">
                <svg className="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="font-heading text-2xl font-bold text-[#C9A961] mb-2">Check Your Email</h2>
              <p className="text-gray-400 text-sm mb-6">
                We sent a password reset link to <strong className="text-white">{email}</strong>. Check your inbox and follow the instructions.
              </p>
              <p className="text-gray-500 text-xs mb-6">Didn&apos;t receive it? Check your spam folder or try again.</p>
              <button
                onClick={() => setSent(false)}
                className="text-[#C9A961] text-sm hover:text-[#e8c97a] transition-colors"
              >
                Try a different email
              </button>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-[#C9A961]">Reset Password</h2>
                <p className="mt-1 text-sm text-gray-400">Enter your email to receive a reset link</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-gray-600 transition-all duration-200 focus:border-[#C9A961]/60 focus:outline-none focus:ring-2 focus:ring-[#C9A961]/20"
                    placeholder="you@organization.com"
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                    <p className="text-xs text-red-400">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-lg bg-[#C9A961] px-6 py-3.5 text-sm font-bold text-[#0F172A] shadow-lg transition-all duration-300 hover:bg-[#e8c97a] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link href="/login" className="text-sm text-[#C9A961] hover:text-[#e8c97a] transition-colors">
                  ← Back to Sign In
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
